import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { useCurrentEmployeeRequest } from "~api/employees.ts";
import { GenericError } from "~components/shared/ErrorPages/GenericError";
import { FullscreenSpinner } from "~components/shared/Loading/FullscreenSpinner/FullscreenSpinner";
import type { Employee } from "~generated";

type CurrentEmployeeContextValue = { currentEmployee: Employee };

export const CurrentEmployeeContext =
  createContext<CurrentEmployeeContextValue | null>(null);

export function CurrentEmployeeProvider({ children }: PropsWithChildren) {
  const { data: currentEmployee, isLoading } = useCurrentEmployeeRequest();

  // A failed refetch keeps the persisted employee; the error page only appears without any data
  if (currentEmployee) {
    return (
      <CurrentEmployeeContext.Provider value={{ currentEmployee }}>
        {children}
      </CurrentEmployeeContext.Provider>
    );
  }

  if (isLoading) {
    return <FullscreenSpinner />;
  }

  return <GenericError />;
}

export function useCurrentEmployee() {
  const currentEmployee = useContext(CurrentEmployeeContext);

  if (!currentEmployee) {
    throw new Error(
      "useCurrentEmployee must be used within a CurrentEmployeeProvider",
    );
  }

  return currentEmployee;
}
